/**
 * Component List Helper
 *
 * Resolves logical keys to the same "name:version" strings used in user_info.activeComponents
 * and in eves_component_list. Resolution order:
 * 1) eves_component_list (catalog from API after login)
 * 2) user_info.activeComponents (fallback if catalog not ready — still comparable with isActive)
 *
 * Do not use TenantComponents; use ComponentKeys or plain string keys only.
 */

import { ComponentKeys } from "../enums/enums";
import {
  getActiveComponentsFromStorage,
  getComponentList,
  getComponentValue,
  resolveComponentByLogicalKey,
} from "./componentListService";

export type ComponentKeyOrValue = keyof typeof ComponentKeys | ComponentKeys | string;

/** Fallbacks when API/cache has no matching row (rare keys, offline, etc.) */
const COMPONENT_FALLBACKS: Record<string, string> = {
  charging_plan: "Charging Plan:1.0",
};

/**
 * If `value` is a ComponentKeys enum member string, return it; otherwise treat as raw key / label.
 */
const normalizeLogicalKey = (value: string): string => {
  const match = (Object.keys(ComponentKeys) as Array<keyof typeof ComponentKeys>).find(
    (k) => ComponentKeys[k] === value
  );
  return match ? ComponentKeys[match] : value;
};

/**
 * True when `input` looks like a full component id "Display Name:semver", not a short logical key.
 */
const looksLikeFullComponentId = (input: string): boolean =>
  input.includes(":") && input.split(":")[0].includes(" ");

/**
 * Resolve any component identifier to its canonical "name:version" string.
 *
 * Accepts three input forms:
 *  - A short logical key      e.g. ComponentKeys.ANALYTICS  → "ANALYTICS"
 *  - A full component id      e.g. "INT - Analytics:1.0"
 *  - A raw label / base name  e.g. "Analytics"
 *
 * Resolution order (first match wins):
 *
 *  1. Exact match in eves_component_list
 *       "INT - Analytics:1.0" already in list → returned as-is.
 *
 *  2. Prefix / base-name match in eves_component_list (case-insensitive substring)
 *       raw = "INT - Analytics" → exact base match → "INT - Analytics:1.0"
 *       raw = "analytics"       → substring match  → "INT - Analytics:1.0"
 *
 *  3. Legacy full component id  (input contains a space AND a colon, e.g. "INT - Analytics:1.0")
 *       Falls through to getComponentValue() by base name, handles version mismatches.
 *
 *  4. Logical key lookup
 *       "ANALYTICS" → normalizeLogicalKey() keeps "ANALYTICS"
 *       → getComponentValue("ANALYTICS") hits LOGICAL_KEY_MATCHERS.ANALYTICS
 *       → matches list entry whose base name contains "analytics" or "reports"
 *       → returns "INT - Analytics:1.0"
 *
 *       If catalog miss, falls back to resolveComponentByLogicalKey() against
 *       user_info.activeComponents so the function still works before the catalog loads.
 *
 *  5. Hardcoded fallbacks (COMPONENT_FALLBACKS map)
 *       e.g. "CHARGING_PLAN" → "Charging Plan:1.0"
 *
 *  6. Passthrough — returns the original input unchanged.
 *
 * @example
 *   // Typical usage — check if a component is enabled for the signed-in user:
 *   const isAnalyticsActive = isActive(getComponentValueSafe(ComponentKeys.ANALYTICS));
 *   // ComponentKeys.ANALYTICS = "ANALYTICS"
 *   // → resolved to "INT - Analytics:1.0" (or whatever the catalog entry is)
 *   // → isActive("INT - Analytics:1.0") checks user_info.activeComponents
 *
 * @example
 *   // Same check outside a React component (with explicit auth context):
 *   const ok = isActiveWithContext(getComponentValueSafe(ComponentKeys.OCPI), authContext);
 *
 * @example
 *   // Passing a raw catalog string directly also works (step 1 exact match):
 *   getComponentValueSafe(ComponentKeys.OCPI); // → "INT - Roaming (OCPI):1.0"
 */
export const getComponentValueSafe = (componentKey: ComponentKeyOrValue | string): string => {
  const raw = String(componentKey).trim();
  const list = getComponentList();

  if (list?.length) {
    if (list.includes(raw)) return raw;
    const base = raw.split(":")[0];
    const baseLo = base.toLowerCase();
    const byPrefix = list.find(
      (c) =>
        c === raw ||
        c.startsWith(`${base}:`) ||
        c.split(":")[0] === base ||
        c.split(":")[0].toLowerCase().includes(baseLo)
    );
    if (byPrefix) return byPrefix;
  }

  if (looksLikeFullComponentId(raw)) {
    const byLegacy = getComponentValue(raw.split(":")[0]) ?? getComponentValue(raw);
    if (byLegacy) return byLegacy;
    return COMPONENT_FALLBACKS[raw] ?? raw;
  }

  const logical = normalizeLogicalKey(raw);
  const fromCatalog = getComponentValue(logical);
  if (fromCatalog) return fromCatalog;

  const fromActive = resolveComponentByLogicalKey(
    getActiveComponentsFromStorage(),
    logical
  );
  if (fromActive) return fromActive;

  return COMPONENT_FALLBACKS[logical] ?? COMPONENT_FALLBACKS[raw] ?? raw;
};

/**
 * Build a role-style map of logical enum key → current full "name:version" from cache.
 */
export const getDynamicTenantComponents = (): Record<string, string> => {
  const dynamicComponents: Record<string, string> = {};
  const enumKeys = Object.keys(ComponentKeys) as Array<keyof typeof ComponentKeys>;
  const active = getActiveComponentsFromStorage();
  enumKeys.forEach((key) => {
    const logical = ComponentKeys[key];
    dynamicComponents[key] =
      getComponentValue(logical) ??
      resolveComponentByLogicalKey(active, logical) ??
      COMPONENT_FALLBACKS[logical] ??
      "";
  });
  return dynamicComponents;
};

/**
 * Check if a component exists in the component list
 * 
 * @param componentValue - The component value to check (e.g., "INT - Roaming (OCPI):1.0")
 * @returns true if component exists, false otherwise
 */
export const hasComponent = (componentValue: string): boolean => {
  const components = getComponentList();
  if (!components?.length) return false;

  const baseName = componentValue.split(":")[0];
  const baseNameLo = baseName.toLowerCase();
  return components.some(
    (c) =>
      c === componentValue ||
      c.split(":")[0] === baseName ||
      c.startsWith(`${baseName}:`) ||
      c.split(":")[0].toLowerCase().includes(baseNameLo)
  );
};
